import { useRef, type KeyboardEvent, type ReactNode } from 'react';
import styles from './Stars.module.css';

const MAX_STARS = 5;

/**
 * §L30's 1–5 stars, for reading only (design card "Ranking"). Filled stars in the warning
 * fill, the rest in the line colour; read out as "4 of 5 stars". `—` when there are none
 * yet (06-qol §3).
 */
export function Stars({ value }: { readonly value: number | null }): ReactNode {
  if (value === null) return <span className={styles.none}>—</span>;
  return (
    <span className={styles.stars} role="img" aria-label={`${value} of ${MAX_STARS} stars`}>
      {Array.from({ length: MAX_STARS }, (_, index) => (
        <span key={index} className={index < value ? styles.on : styles.off} aria-hidden>
          ★
        </span>
      ))}
    </span>
  );
}

interface StarRatingProps {
  /** What is rated, for the group's name: "Trainability". */
  readonly label: string;
  readonly value: number | null;
  /** Null takes the rating away. */
  readonly onChange: (value: number | null) => void;
  /** A star's description from the matrix, keyed "1"…"5" (JB-12's 1, 3 and 5). */
  readonly hints?: Readonly<Record<string, string>>;
  readonly isDisabled?: boolean;
}

/**
 * R6 HR's 1–5 on a matrix line (§L31, design card "Review and shortlist"): a radio group of five
 * stars. Click or Enter picks; the chosen star again, or Backspace, clears; ←/→ move and pick.
 * One tab stop, on the chosen star or the first.
 */
export function StarRating({ label, value, onChange, hints = {}, isDisabled = false }: StarRatingProps): ReactNode {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  function pick(next: number): void {
    onChange(next === value ? null : next);
  }

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>, star: number): void {
    const step = event.key === 'ArrowRight' || event.key === 'ArrowUp' ? 1 : event.key === 'ArrowLeft' || event.key === 'ArrowDown' ? -1 : 0;
    if (step !== 0) {
      event.preventDefault();
      const next = Math.min(MAX_STARS, Math.max(1, star + step));
      onChange(next);
      refs.current[next - 1]?.focus();
      return;
    }
    if (event.key === 'Backspace' || event.key === 'Delete') {
      event.preventDefault();
      onChange(null);
    }
  }

  return (
    <span className={styles.rating} role="radiogroup" aria-label={label}>
      {Array.from({ length: MAX_STARS }, (_, index) => {
        const star = index + 1;
        const hint = hints[String(star)];
        return (
          <button
            key={star}
            ref={(element) => {
              refs.current[index] = element;
            }}
            type="button"
            role="radio"
            aria-checked={value === star}
            aria-label={hint ? `${star} of ${MAX_STARS}: ${hint}` : `${star} of ${MAX_STARS}`}
            title={hint}
            tabIndex={(value ?? 1) === star ? 0 : -1}
            className={styles.star}
            data-on={value !== null && star <= value ? '' : undefined}
            disabled={isDisabled}
            onClick={() => pick(star)}
            onKeyDown={(event) => onKeyDown(event, star)}
          >
            ★
          </button>
        );
      })}
    </span>
  );
}
